import { ClientCombobox } from "@/components/ClientCombobox";
import { useEffect, useState, useRef, useCallback } from "react";
import { DropZone } from "@/components/DropZone";
import { useParams, useNavigate } from "react-router-dom";
import { api, API, formatApiErrorDetail, fmtDate, openAuthPdf } from "@/lib/api";
import { useSuspendedClientGuard } from "@/context/SuspendedClientAlert";
import { useAuth } from "@/context/AuthContext";
import { InternalLayout } from "@/components/InternalLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeftIcon as ArrowLeft, PrinterIcon as Printer, FloppyDiskIcon as FloppyDisk, TrashIcon as Trash, ClockIcon as Clock, WarningCircleIcon as WarningCircle, PaperclipIcon as Paperclip, UploadIcon as Upload, ArrowRightIcon as ArrowRight, PlusIcon as Plus } from "@phosphor-icons/react";
import { RIP_STATI, RIP_STATO_COLOR, RIP_TIPI, RIP_ACCESSORI, RIP_NEXT } from "@/lib/riparazioni";

const DateRow = ({ label, value }) => (
  <div className="flex justify-between text-sm py-1.5 border-b border-border/60 last:border-0">
    <span className="text-muted-foreground">{label}</span>
    <span className="tabular-nums font-medium">{value ? fmtDate(value) : "—"}</span>
  </div>
);

export default function RepairDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [r, setR] = useState(null);
  useSuspendedClientGuard(r?.cliente_id);
  const [tecnici, setTecnici] = useState([]);
  const [clients, setClients] = useState([]);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    api.get(`/riparazioni/${id}`).then(({ data }) => setR(data)).catch(() => { toast.error("Riparazione non trovata"); nav("/riparazioni"); });
  }, [id, nav]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/tecnici").then(({ data }) => setTecnici(data)).catch(() => {}); }, []);
  useEffect(() => { if (isAdmin) api.get("/clients").then(({ data }) => setClients(data)).catch(() => {}); }, [isAdmin]);

  const upd = (k, v) => setR((x) => ({ ...x, [k]: v }));
  const toggleAcc = (a) => setR((x) => ({
    ...x, accessori_consegnati: (x.accessori_consegnati || []).includes(a)
      ? x.accessori_consegnati.filter((y) => y !== a) : [...(x.accessori_consegnati || []), a],
  }));

  const buildPayload = (over = {}) => ({
    data_ritiro: r.data_ritiro, operatore_ritiro: r.operatore_ritiro, stato: r.stato,
    cliente_id: r.cliente_id, cliente_nome: r.cliente_nome, referente: r.referente, telefono: r.telefono, email: r.email,
    tipologia_dispositivo: r.tipologia_dispositivo, marca: r.marca, modello: r.modello, seriale: r.seriale, asset_tag: r.asset_tag, password_dispositivo: r.password_dispositivo,
    problema_dichiarato: r.problema_dichiarato, accessori_consegnati: r.accessori_consegnati || [],
    condizioni_estetiche: r.condizioni_estetiche, note_reception: r.note_reception,
    tecnico_assegnato: r.tecnico_assegnato, diagnosi_tecnica: r.diagnosi_tecnica, lavorazione_eseguita: r.lavorazione_eseguita,
    note_tecniche_interne: r.note_tecniche_interne, note_cliente: r.note_cliente,
    operatore_riconsegna: r.operatore_riconsegna, note_riconsegna: r.note_riconsegna,
    prezzo_manodopera: r.prezzo_manodopera ?? 0,
    prodotti: (r.prodotti || []).map((p) => ({ nome: p.nome || "", quantita: Number(p.quantita) || 0, prezzo_unitario: Number(p.prezzo_unitario) || 0 })),
    data_presa_in_carico: r.data_presa_in_carico, data_inizio_lavorazione: r.data_inizio_lavorazione,
    data_pronto_riconsegna: r.data_pronto_riconsegna, data_riconsegna: r.data_riconsegna,
    ...over,
  });

  const save = async (over = {}, silent = false) => {
    setSaving(true);
    try {
      const { data } = await api.put(`/riparazioni/${id}`, buildPayload(over));
      setR(data);
      if (!silent) toast.success("Riparazione aggiornata");
      return true;
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); return false; }
    finally { setSaving(false); }
  };

  const changeState = async (newStato) => {
    await save({ stato: newStato });
  };

  const remove = async () => {
    if (!window.confirm(`Eliminare la pratica ${r.numero_pratica}? L'operazione è irreversibile.`)) return;
    try { await api.delete(`/riparazioni/${id}`); toast.success("Riparazione eliminata"); nav("/riparazioni"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const uploadFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    try {
      const { data } = await api.post(`/riparazioni/${id}/allegati`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setR((x) => ({ ...x, allegati: data.allegati }));
      toast.success("Allegato caricato");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    e.target.value = "";
  };
  const delAllegato = async (aid) => {
    if (!window.confirm("Eliminare l'allegato?")) return;
    try { const { data } = await api.delete(`/riparazioni/${id}/allegati/${aid}`); setR((x) => ({ ...x, allegati: data.allegati })); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  if (!r) return <InternalLayout><div className="p-12 text-muted-foreground">Caricamento…</div></InternalLayout>;

  return (
    <InternalLayout>
      <div className="p-8 lg:p-12 max-w-6xl mx-auto">
        {/* Command bar */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <Button variant="ghost" size="icon" data-testid="rip-detail-back" onClick={() => nav(-1)} className="h-8 w-8"><ArrowLeft size={18} /></Button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-heading text-xl font-bold">{r.numero_pratica}</h1>
              <Badge variant="outline" className={`rounded-sm ${RIP_STATO_COLOR[r.stato] || ""}`} data-testid="rip-detail-stato-badge">{r.stato}</Badge>
              {r.giorni_da_ritiro != null && (
                <span className={`inline-flex items-center gap-1 text-sm ${r.badge_ritardo ? "text-rose-600 font-semibold" : "text-muted-foreground"}`}>
                  {r.badge_ritardo ? <WarningCircle size={15} weight="fill" /> : <Clock size={15} />} aperta da {r.giorni_da_ritiro} giorni
                </span>
              )}
            </div>
            <div className="text-sm text-muted-foreground mt-0.5">{r.cliente_nome} · {r.tipologia_dispositivo} {r.marca} {r.modello}</div>
          </div>
          <Button variant="outline" className="rounded-sm gap-2" data-testid="rip-print-scheda" onClick={() => openAuthPdf(`/riparazioni/${id}/scheda-ritiro-pdf`)}><Printer size={16} /> Stampa scheda di ritiro</Button>
          <Button data-testid="rip-detail-save" onClick={() => save()} disabled={saving} className="rounded-sm gap-2"><FloppyDisk size={16} /> Salva</Button>
          <Button variant="ghost" size="icon" onClick={remove} data-testid="rip-detail-delete" className="h-9 w-9 text-muted-foreground hover:text-destructive"><Trash size={18} /></Button>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <Tabs defaultValue="accettazione">
              <TabsList className="rounded-sm">
                <TabsTrigger value="accettazione" data-testid="rip-tab-accettazione">Accettazione</TabsTrigger>
                <TabsTrigger value="lavorazione" data-testid="rip-tab-lavorazione">Diagnosi / Lavorazione</TabsTrigger>
                <TabsTrigger value="prezzi" data-testid="rip-tab-prezzi">Prezzi</TabsTrigger>
                <TabsTrigger value="riconsegna" data-testid="rip-tab-riconsegna">Riconsegna</TabsTrigger>
                <TabsTrigger value="storico" data-testid="rip-tab-storico">Storico stati</TabsTrigger>
                <TabsTrigger value="allegati" data-testid="rip-tab-allegati">Allegati</TabsTrigger>
              </TabsList>

              <TabsContent value="accettazione">
                <Card className="rounded-sm border-border shadow-none p-5 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <Label className="text-xs uppercase tracking-wider">Cliente</Label>
                      {isAdmin ? (
                        <>
                          <div className="mt-1"><ClientCombobox clients={clients} value={r.cliente_id || "none"} extraOptions={[{ value: "none", label: "— Senza anagrafica (nome libero) —" }]} placeholder="Seleziona cliente" testid="rip-cliente-select" onChange={(v) => {
                            if (v === "none") { setR((x) => ({ ...x, cliente_id: "" })); return; }
                            const c = clients.find((x) => x.id === v);
                            setR((x) => ({ ...x, cliente_id: v, cliente_nome: c?.ragione_sociale || x.cliente_nome, telefono: c?.telefono || x.telefono, email: c?.email || x.email }));
                          }} /></div>
                          {!r.cliente_id && (
                            <Input value={r.cliente_nome || ""} onChange={(e) => upd("cliente_nome", e.target.value)} className="mt-2 rounded-sm" data-testid="rip-cliente-nome-libero" placeholder="Nome cliente (senza anagrafica)" />
                          )}
                        </>
                      ) : (
                        <Input value={r.cliente_nome || ""} className="mt-1 rounded-sm" disabled data-testid="rip-cliente-readonly" />
                      )}
                    </div>
                    <div><Label className="text-xs uppercase tracking-wider">Referente</Label><Input value={r.referente || ""} onChange={(e) => upd("referente", e.target.value)} className="mt-1 rounded-sm" /></div>
                    <div><Label className="text-xs uppercase tracking-wider">Telefono</Label><Input value={r.telefono || ""} onChange={(e) => upd("telefono", e.target.value)} className="mt-1 rounded-sm" /></div>
                    <div><Label className="text-xs uppercase tracking-wider">Email</Label><Input value={r.email || ""} onChange={(e) => upd("email", e.target.value)} className="mt-1 rounded-sm" /></div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider">Tipologia</Label>
                      <Select value={r.tipologia_dispositivo} onValueChange={(v) => upd("tipologia_dispositivo", v)}>
                        <SelectTrigger className="mt-1 rounded-sm capitalize"><SelectValue /></SelectTrigger>
                        <SelectContent>{RIP_TIPI.map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><Label className="text-xs uppercase tracking-wider">Marca</Label><Input value={r.marca || ""} onChange={(e) => upd("marca", e.target.value)} className="mt-1 rounded-sm" /></div>
                    <div><Label className="text-xs uppercase tracking-wider">Modello</Label><Input value={r.modello || ""} onChange={(e) => upd("modello", e.target.value)} className="mt-1 rounded-sm" /></div>
                    <div><Label className="text-xs uppercase tracking-wider">Seriale</Label><Input value={r.seriale || ""} onChange={(e) => upd("seriale", e.target.value)} className="mt-1 rounded-sm" /></div>
                    <div><Label className="text-xs uppercase tracking-wider">Asset tag</Label><Input value={r.asset_tag || ""} onChange={(e) => upd("asset_tag", e.target.value)} className="mt-1 rounded-sm" /></div>
                    <div><Label className="text-xs uppercase tracking-wider">Password dispositivo</Label><Input value={r.password_dispositivo || ""} onChange={(e) => upd("password_dispositivo", e.target.value)} className="mt-1 rounded-sm" data-testid="rip-password" placeholder="PIN / password di accesso" /></div>
                    <div><Label className="text-xs uppercase tracking-wider">Condizioni estetiche</Label><Input value={r.condizioni_estetiche || ""} onChange={(e) => upd("condizioni_estetiche", e.target.value)} className="mt-1 rounded-sm" /></div>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider">Accessori consegnati</Label>
                    <div className="flex flex-wrap gap-2 mt-1.5">
                      {RIP_ACCESSORI.map((a) => (
                        <button key={a} type="button" onClick={() => toggleAcc(a)}
                          className={`px-3 py-1 rounded-full text-sm border capitalize transition-colors ${(r.accessori_consegnati || []).includes(a) ? "bg-primary text-primary-foreground border-primary" : "bg-white border-border text-muted-foreground"}`}>{a}</button>
                      ))}
                    </div>
                  </div>
                  <div><Label className="text-xs uppercase tracking-wider">Problema dichiarato</Label><Textarea value={r.problema_dichiarato || ""} onChange={(e) => upd("problema_dichiarato", e.target.value)} className="mt-1 rounded-sm" rows={2} /></div>
                  <div><Label className="text-xs uppercase tracking-wider">Note reception</Label><Textarea value={r.note_reception || ""} onChange={(e) => upd("note_reception", e.target.value)} className="mt-1 rounded-sm" rows={2} /></div>
                </Card>
              </TabsContent>

              <TabsContent value="lavorazione">
                <Card className="rounded-sm border-border shadow-none p-5 space-y-4">
                  <div>
                    <Label className="text-xs uppercase tracking-wider">Tecnico assegnato</Label>
                    <Select value={r.tecnico_assegnato || "none"} onValueChange={(v) => upd("tecnico_assegnato", v === "none" ? "" : v)}>
                      <SelectTrigger className="mt-1 rounded-sm" data-testid="rip-tecnico"><SelectValue placeholder="Seleziona tecnico" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Non assegnato</SelectItem>
                        {tecnici.map((t) => <SelectItem key={t.id} value={t.name}>{t.name}{t.role === "admin" ? " · admin" : ""}</SelectItem>)}
                        {r.tecnico_assegnato && !tecnici.some((t) => t.name === r.tecnico_assegnato) && (
                          <SelectItem value={r.tecnico_assegnato}>{r.tecnico_assegnato}</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-xs uppercase tracking-wider">Diagnosi tecnica</Label><Textarea value={r.diagnosi_tecnica || ""} onChange={(e) => upd("diagnosi_tecnica", e.target.value)} className="mt-1 rounded-sm" rows={3} /></div>
                  <div><Label className="text-xs uppercase tracking-wider">Lavorazione eseguita</Label><Textarea value={r.lavorazione_eseguita || ""} onChange={(e) => upd("lavorazione_eseguita", e.target.value)} className="mt-1 rounded-sm" rows={3} /></div>
                  <div><Label className="text-xs uppercase tracking-wider">Note tecniche interne</Label><Textarea value={r.note_tecniche_interne || ""} onChange={(e) => upd("note_tecniche_interne", e.target.value)} className="mt-1 rounded-sm" rows={2} /></div>
                  <div><Label className="text-xs uppercase tracking-wider">Note per il cliente</Label><Textarea value={r.note_cliente || ""} onChange={(e) => upd("note_cliente", e.target.value)} className="mt-1 rounded-sm" rows={2} /></div>
                </Card>
              </TabsContent>

              <TabsContent value="prezzi">
                <Card className="rounded-sm border-border shadow-none p-5 space-y-4" data-testid="rip-prezzi-card">
                  <h3 className="font-heading font-semibold text-sm uppercase tracking-wider text-primary">Prezzi</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs uppercase tracking-wider">Manodopera (€)</Label>
                      <Input type="number" min="0" step="0.01" value={r.prezzo_manodopera ?? ""} onChange={(e) => upd("prezzo_manodopera", e.target.value === "" ? 0 : parseFloat(e.target.value))} className="mt-1 rounded-sm" data-testid="rip-prezzo-manodopera" />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs uppercase tracking-wider">Prodotti</Label>
                      <Button type="button" size="sm" variant="outline" className="rounded-sm h-7 text-xs" data-testid="rip-add-prodotto"
                        onClick={() => upd("prodotti", [...(r.prodotti || []), { nome: "", quantita: 1, prezzo_unitario: 0 }])}>
                        <Plus className="h-3.5 w-3.5 mr-1" /> Aggiungi prodotto
                      </Button>
                    </div>
                    {(r.prodotti || []).length > 0 && (
                      <div className="grid grid-cols-[1fr_90px_120px_120px_36px] gap-2 items-center text-[10px] uppercase tracking-wider text-muted-foreground px-1">
                        <span>Nome prodotto</span><span className="text-right">Qt.</span><span className="text-right">Prezzo unit. (€)</span><span className="text-right">Totale (€)</span><span />
                      </div>
                    )}
                    {(r.prodotti || []).map((p, i) => {
                      const updP = (k, v) => upd("prodotti", (r.prodotti || []).map((x, j) => j === i ? { ...x, [k]: v } : x));
                      const rowTot = (Number(p.quantita) || 0) * (Number(p.prezzo_unitario) || 0);
                      return (
                        <div key={i} className="grid grid-cols-[1fr_90px_120px_120px_36px] gap-2 items-center" data-testid={`rip-prodotto-row-${i}`}>
                          <Input value={p.nome || ""} onChange={(e) => updP("nome", e.target.value)} placeholder="Nome prodotto" className="rounded-sm" data-testid={`rip-prodotto-nome-${i}`} />
                          <Input type="number" min="0" step="1" value={p.quantita ?? ""} onChange={(e) => updP("quantita", e.target.value === "" ? 0 : parseFloat(e.target.value))} className="rounded-sm text-right" data-testid={`rip-prodotto-qta-${i}`} />
                          <Input type="number" min="0" step="0.01" value={p.prezzo_unitario ?? ""} onChange={(e) => updP("prezzo_unitario", e.target.value === "" ? 0 : parseFloat(e.target.value))} className="rounded-sm text-right" data-testid={`rip-prodotto-prezzo-${i}`} />
                          <span className="text-right text-sm tabular-nums" data-testid={`rip-prodotto-totale-${i}`}>{rowTot.toFixed(2)}</span>
                          <Button type="button" size="icon" variant="ghost" className="h-8 w-8 text-destructive" data-testid={`rip-prodotto-del-${i}`}
                            onClick={() => upd("prodotti", (r.prodotti || []).filter((_, j) => j !== i))}>
                            <Trash className="h-4 w-4" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>

                  <div className="border-t border-border pt-3 flex flex-col items-end gap-1 text-sm">
                    {(() => {
                      const totProdotti = (r.prodotti || []).reduce((s, p) => s + (Number(p.quantita) || 0) * (Number(p.prezzo_unitario) || 0), 0);
                      const totGen = (Number(r.prezzo_manodopera) || 0) + totProdotti;
                      return (
                        <>
                          <div className="flex gap-6 text-muted-foreground"><span>Manodopera</span><span className="tabular-nums w-24 text-right">€ {(Number(r.prezzo_manodopera) || 0).toFixed(2)}</span></div>
                          <div className="flex gap-6 text-muted-foreground"><span>Prodotti</span><span className="tabular-nums w-24 text-right">€ {totProdotti.toFixed(2)}</span></div>
                          <div className="flex gap-6 font-semibold text-foreground"><span>Totale</span><span className="tabular-nums w-24 text-right" data-testid="rip-prezzi-totale">€ {totGen.toFixed(2)}</span></div>
                        </>
                      );
                    })()}
                  </div>
                </Card>
              </TabsContent>

              <TabsContent value="riconsegna">
                <Card className="rounded-sm border-border shadow-none p-5 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label className="text-xs uppercase tracking-wider">Operatore riconsegna</Label><Input value={r.operatore_riconsegna || ""} onChange={(e) => upd("operatore_riconsegna", e.target.value)} className="mt-1 rounded-sm" /></div>
                    <div><Label className="text-xs uppercase tracking-wider">Data riconsegna</Label><Input type="date" value={(r.data_riconsegna || "").slice(0, 10)} onChange={(e) => upd("data_riconsegna", e.target.value)} className="mt-1 rounded-sm" /></div>
                  </div>
                  <div><Label className="text-xs uppercase tracking-wider">Note riconsegna</Label><Textarea value={r.note_riconsegna || ""} onChange={(e) => upd("note_riconsegna", e.target.value)} className="mt-1 rounded-sm" rows={2} /></div>
                </Card>
              </TabsContent>

              <TabsContent value="storico">
                <Card className="rounded-sm border-border shadow-none p-5">
                  <div className="space-y-3" data-testid="rip-storico-list">
                    {(r.storico_stati || []).slice().reverse().map((s, i) => (
                      <div key={i} className="flex items-start gap-3 text-sm">
                        <div className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 shrink-0" />
                        <div>
                          <div className="font-medium">{s.stato_precedente ? `${s.stato_precedente} → ` : ""}{s.nuovo_stato}</div>
                          <div className="text-xs text-muted-foreground">{new Date(s.data).toLocaleString("it-IT")}{s.utente ? ` · ${s.utente}` : ""}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              </TabsContent>

              <TabsContent value="allegati">
                <DropZone onFiles={(fl) => uploadFiles({ target: { files: fl, value: "" } })} className="block">
                <Card className="rounded-sm border-border shadow-none p-5">
                  <div className="flex items-center justify-between mb-3">
                    <Label className="text-xs uppercase tracking-wider">Allegati</Label>
                    <input type="file" multiple ref={fileRef} className="hidden" onChange={uploadFiles} />
                    <Button variant="outline" size="sm" className="rounded-sm gap-1.5" data-testid="rip-upload-allegato" onClick={() => fileRef.current?.click()}><Upload size={14} /> Carica file</Button>
                  </div>
                  {(r.allegati || []).length === 0 && <p className="text-sm text-muted-foreground">Nessun allegato.</p>}
                  <div className="space-y-2">
                    {(r.allegati || []).map((a) => (
                      <div key={a.id} className="flex items-center gap-2 bg-secondary/50 rounded-sm p-2 text-sm" data-testid="rip-allegato-row">
                        <Paperclip size={15} className="text-muted-foreground shrink-0" />
                        <a href={`${API}/riparazioni/${id}/allegati/${a.id}/download`} className="flex-1 truncate hover:underline text-primary">{a.nome_originale}</a>
                        <span className="text-xs text-muted-foreground">{Math.round((a.size || 0) / 1024)} KB</span>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={() => delAllegato(a.id)}><Trash size={14} /></Button>
                      </div>
                    ))}
                  </div>
                </Card>
                </DropZone>
              </TabsContent>
            </Tabs>
          </div>

          {/* Sidebar dati */}
          <div className="space-y-6">
            <Card className="rounded-sm border-border shadow-none p-5">
              <Label className="text-xs uppercase tracking-wider">Stato</Label>
              <Select value={r.stato} onValueChange={(v) => changeState(v)}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="rip-stato-select"><SelectValue /></SelectTrigger>
                <SelectContent>{RIP_STATI.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </Card>

            <Card className="rounded-sm border-border shadow-none p-5">
              <h4 className="font-heading font-semibold text-sm mb-2">Tempi</h4>
              <DateRow label="Ritiro" value={r.data_ritiro} />
              <DateRow label="Presa in carico" value={r.data_presa_in_carico} />
              <DateRow label="Inizio lavorazione" value={r.data_inizio_lavorazione} />
              <DateRow label="Pronto riconsegna" value={r.data_pronto_riconsegna} />
              <DateRow label="Riconsegna" value={r.data_riconsegna} />
              <div className="mt-3 pt-3 border-t border-border/60 space-y-1 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Giorni aperta</span><span className="tabular-nums font-medium">{r.giorni_da_ritiro ?? "—"}</span></div>
                {r.giorni_ritiro_riconsegna != null && <div className="flex justify-between"><span className="text-muted-foreground">Ritiro → riconsegna</span><span className="tabular-nums font-medium">{r.giorni_ritiro_riconsegna}g</span></div>}
                {r.giorni_pronto_riconsegna != null && <div className="flex justify-between"><span className="text-muted-foreground">Pronto → riconsegna</span><span className="tabular-nums font-medium">{r.giorni_pronto_riconsegna}g</span></div>}
              </div>
            </Card>
          </div>
        </div>
      </div>
    </InternalLayout>
  );
}
